import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HeartCrack, Search, AlertCircle, CreditCard, Download, Shield, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { toPng } from "html-to-image";
import { API } from "@/utils/api";

const MarkAsEndedPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: lookup, 2: confirm & pay, 3: success
  const [certificateId, setCertificateId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState("");
  const [pricing, setPricing] = useState({ currency: "USD", amount: 0.89, original_amount: 1.89, symbol: "$" });
  const [isProcessing, setIsProcessing] = useState(false);
  const [endedData, setEndedData] = useState(null);
  const [fullCertificate, setFullCertificate] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const certificateRef = useRef(null);

  useEffect(() => {
    // Fetch pricing
    axios.get(`${API}/geolocation`).then(res => setPricing(res.data)).catch(() => {});
  }, []);

  const handleLookup = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.get(`${API}/certificates/lookup/${certificateId.toUpperCase()}`);
      setCertificate(response.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || "Certificate not found. Please check your LoBe ID.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    
    try {
      // Create payment order
      const orderResponse = await axios.post(`${API}/certificates/end/create-order`, {
        certificate_id: certificate.certificate_id
      }, {
        params: { currency: pricing.currency }
      });
      
      const orderData = orderResponse.data;
      
      // Check if it's a mock payment
      if (orderData.mock) {
        toast.info("Demo mode: Simulating payment...");
        
        // Mock verify payment
        const verifyResponse = await axios.post(
          `${API}/certificates/end/mock-success?certificate_id=${certificate.certificate_id}`
        );
        
        setEndedData(verifyResponse.data);
        // Fetch full certificate data for display
        try {
          const certRes = await axios.get(`${API}/certificates/${certificate.certificate_id}`);
          setFullCertificate(certRes.data);
        } catch (e) {
          console.error('Could not fetch full certificate');
        }
        setStep(3);
        toast.success("Payment successful!");
        return;
      }
      
      // Load Razorpay SDK
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
      
      script.onload = () => {
        const options = {
          key: orderData.key,
          amount: orderData.amount,
          currency: orderData.currency,
          name: "LoBe",
          description: "Mark Relationship as Ended",
          order_id: orderData.order_id,
          handler: async (res) => {
            try {
              const verifyResponse = await axios.post(`${API}/certificates/end/verify`, {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
                certificate_id: certificate.certificate_id
              });
              
              setEndedData(verifyResponse.data);
              // Fetch full certificate data for display
              try {
                const certRes = await axios.get(`${API}/certificates/${certificate.certificate_id}`);
                setFullCertificate(certRes.data);
              } catch (e) {
                console.error('Could not fetch full certificate');
              }
              setStep(3);
              toast.success("Payment successful!");
            } catch (error) {
              toast.error("Payment verification failed. Please contact support.");
            }
          },
          theme: { color: "#E8A0BF" },
          modal: {
            ondismiss: () => {
              setIsProcessing(false);
              toast.error("Payment cancelled");
            }
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      };
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed. Please try again.");
      setIsProcessing(false);
    }
  };

  const handleDownloadImage = async () => {
    setIsDownloading(true);
    try {
      if (certificateRef.current) {
        const dataUrl = await toPng(certificateRef.current, {
          quality: 1,
          pixelRatio: 3, // High quality for crisp images
          backgroundColor: '#F9F6F2',
          cacheBust: true,
        });
        
        const link = document.createElement('a');
        link.download = `LoBe-Certificate-${fullCertificate?.your_name || 'Certificate'}-${fullCertificate?.partner_name || ''}.png`;
        link.href = dataUrl;
        link.click();
        
        toast.success("Certificate downloaded successfully!");
      } else {
        throw new Error("Certificate element not found");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download certificate. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="mark-ended-page">
      <div className="max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full text-sm text-red-500 mb-4">
              <HeartCrack className="w-4 h-4" />
              <span>Mark as Ended</span>
            </div>
            <h1 
              className="text-2xl sm:text-3xl font-bold text-[#2D2D2D] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              End Your Certificate
            </h1>
            <p className="text-sm text-[#5A5A5A] max-w-md mx-auto">
              If your relationship has ended, you can mark your certificate as ended. 
              This will add an "Ended" status to your certificate.
            </p>
          </div>

          {/* Step 1: Lookup */}
          {step === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-[#E2E2E2]/50">
              <form onSubmit={handleLookup} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="certificateId" className="text-[#2D2D2D] font-medium">
                    Enter Your LoBe ID
                  </Label>
                  <Input
                    id="certificateId"
                    value={certificateId}
                    onChange={(e) => setCertificateId(e.target.value.toUpperCase())}
                    placeholder="LOBE-XXXXXXXX"
                    className="h-12 font-mono text-center uppercase border-[#E2E2E2] focus:border-[#E8A0BF]"
                    data-testid="input-certificate-id"
                  />
                  <p className="text-xs text-[#888888]">
                    Your LoBe ID can be found on your certificate or proof page.
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading || !certificateId}
                  className="w-full bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full py-5"
                  data-testid="lookup-btn"
                >
                  {isLoading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Looking up...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Find Certificate
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Step 2: Confirm & Pay */}
          {step === 2 && certificate && (
            <div className="space-y-6">
              {/* Certificate Info */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-[#E2E2E2]/50">
                <h3 className="font-semibold text-[#2D2D2D] mb-4">Certificate Found</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Certificate ID:</span>
                    <span className="font-mono text-[#2D2D2D]">{certificate.certificate_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Names:</span>
                    <span className="text-[#2D2D2D]">{certificate.your_name} & {certificate.partner_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Created:</span>
                    <span className="text-[#2D2D2D]">{formatDate(certificate.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                <p className="text-amber-800 font-medium mb-2">⚠️ Important:</p>
                <ul className="text-amber-700 space-y-1 list-disc list-inside">
                  <li>This action cannot be undone</li>
                  <li>Your certificate will be marked as "Ended"</li>
                  <li>The ended date will be permanently recorded</li>
                </ul>
              </div>

              {/* Payment */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-[#E2E2E2]/50">
                <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-lg">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700">Secure payment</span>
                </div>

                <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#E2E2E2]">
                  <span className="text-[#5A5A5A]">Fee to mark as ended:</span>
                  <div className="text-right">
                    <span className="text-sm text-[#888888] line-through mr-2">
                      {pricing.symbol}{pricing.currency === "INR" ? (pricing.original_amount || 99) : (pricing.original_amount || 1.89).toFixed(2)}
                    </span>
                    <span className="text-2xl font-bold text-[#E8A0BF]">
                      {pricing.symbol}{pricing.currency === "INR" ? pricing.amount : pricing.amount.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={isProcessing}
                  className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full py-5"
                  data-testid="pay-end-btn"
                >
                  {isProcessing ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay & Mark as Ended
                    </>
                  )}
                </Button>

                <button
                  onClick={() => { setStep(1); setCertificate(null); setError(""); }}
                  className="w-full mt-3 text-sm text-[#888888] hover:text-[#5A5A5A]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && endedData && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-[#E2E2E2]/50 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HeartCrack className="w-8 h-8 text-red-500" />
                </div>
                
                <h3 className="text-xl font-semibold text-[#2D2D2D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Certificate Updated
                </h3>
                
                <p className="text-[#5A5A5A] mb-6">
                  Your certificate has been marked as ended on {formatDate(endedData.ended_at)}.
                </p>

                {/* Action Buttons Only - No Preview */}
                <div className="space-y-3 max-w-md mx-auto">
                  <Button
                    onClick={() => navigate(`/proof/${endedData.certificate_id}`)}
                    className="w-full bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full py-5"
                    data-testid="view-certificate-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Certificate
                  </Button>

                  <Button
                    onClick={() => {
                      // Navigate to proof page to download
                      window.open(`/proof/${endedData.certificate_id}`, '_blank');
                    }}
                    variant="outline"
                    className="w-full rounded-full py-5 border-[#E8A0BF] text-[#E8A0BF]"
                    data-testid="download-ended-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Certificate
                  </Button>
                </div>

                <p className="text-xs text-[#888888] mt-4">
                  The certificate now shows "Ended" status with the date.
                </p>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <p className="text-xs text-[#888888] text-center mt-6">
            LoBe certificates are symbolic and not legal documents.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default MarkAsEndedPage;
